/**
 * @fileoverview Per-job Mail route (MOR-529).
 *
 * URL: /dashboard/jobs/:jobId/mail
 *  - ?thread=<mailThreads id> deep-links a thread open in the reader
 */

import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { Loader2 } from "lucide-react";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { JobMailboxPage } from "@/components/mail/JobMailboxPage";

interface MailSearch {
  thread?: string;
  /** Deep-links a user draft open in the composer (from the workspace page). */
  draft?: string;
  /**
   * Settings view flag, kept in the URL so clicking the sidebar "Mail" entry
   * (which navigates here with no search) always returns to the inbox.
   */
  settings?: true;
}

export const Route = createFileRoute(
  "/_app/_auth/dashboard/_layout/jobs/$jobId/mail",
)({
  component: JobMailRoute,
  validateSearch: (search: Record<string, unknown>): MailSearch => ({
    thread: typeof search.thread === "string" ? search.thread : undefined,
    draft: typeof search.draft === "string" ? search.draft : undefined,
    settings:
      search.settings === true || search.settings === "true" ? true : undefined,
  }),
});

function JobMailRoute() {
  const { jobId } = Route.useParams();

  const { data: job, isLoading } = useQuery(
    convexQuery(api.jobs.queries.getJob, { jobId: jobId as Id<"jobs"> }),
  );

  if (isLoading || job === undefined) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return <JobMailboxPage jobId={jobId as Id<"jobs">} />;
}
